import * as neo4j from "neo4j-driver";
import { Driver, Session } from "neo4j-driver";
import { typedEntries, typedKeys } from "../../../../core/src/core";

export type Neo4jParsedRecord = {
  classLabel: string;
};

export class Neo4jClient {
  private driver: Driver;

  constructor(uri: string, username: string, password: string) {
    this.driver = neo4j.driver(uri, neo4j.auth.basic(username, password));
  }

  private async withSession<T>(
    fct: (session: Session) => Promise<T>
  ): Promise<T> {
    const session = this.driver.session({ database: "neo4j" });
    try {
      const result = await fct(session);
      await session.close();
      return result;
    } catch (err) {
      await session.close();
      throw err;
    }
  }

  async newNode(record: {}, classLabel: string, functionKeys?: string[]) {
    const writeQuery = `CREATE (p1:${Neo4jClient.sanitizeClassLabel(
      classLabel
    )} { ${typedKeys(record)
      .map(
        (key) =>
          `${key}:${functionKeys?.includes(key) ? record[key] : `$${key}`}`
      )
      .join(",")} })
    RETURN p1`;
    return this.write(writeQuery, record);
  }

  async write(writeQuery: string, parameters: {}) {
    return this.withSession(async (session) => {
      const writeResult = await session.executeWrite((tx) =>
        tx.run(writeQuery, parameters)
      );
      return writeResult.records;
    });
  }

  async read(readQuery: string, parameters: {}) {
    return this.withSession(async (session) => {
      const readResult = await session.executeRead((tx) =>
        tx.run(readQuery, parameters)
      );
      return readResult.records;
    });
  }
  async readRecords(
    readQuery: string,
    parameters: {},
    namedRecord = "p"
  ): Promise<Neo4jParsedRecord[]> {
    const records = await this.read(readQuery, parameters);
    return records.map((record) => {
      const node = record.get(namedRecord);
      const mappedProps = Object.fromEntries(
        typedEntries(node.properties).map(([key, value]) => {
          const className = value.constructor.name;
          if (className === "Integer2" || className === "Integer") {
            value = value.toNumber();
          }
          return [key, value];
        })
      );
      return {
        ...mappedProps,
        classLabel: node.labels[0],
      };
    });
  }
  static sanitizeClassLabel(classLabel: string): string {
    return classLabel.replaceAll(new RegExp("[^a-z0-9]", "gi"), "_");
  }
}
